import { ensureContrast } from '@ardoq/color-helpers';
import type {
  DependencyMapNode,
  DependencyMapNodeViewModel,
  DependencyMapReferenceViewModel,
} from './types';
import {
  componentUtils,
  referenceUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import type { EnhancedScopeData } from '@ardoq/data-model';

export const isViewReference = (
  reference: DependencyMapReferenceViewModel | DependencyMapNodeViewModel
): reference is DependencyMapReferenceViewModel =>
  Boolean((reference as DependencyMapReferenceViewModel).referenceTypeId);

export function* nodeReferences(node: DependencyMapNode) {
  if (node.combinedReferences) {
    yield* node.combinedReferences;
  }
  if (node.incomingReferences) {
    yield* node.incomingReferences;
  }
  if (node.outgoingReferences) {
    yield* node.outgoingReferences;
  }
}

export function* referencedNodes(node: DependencyMapNode) {
  for (const reference of nodeReferences(node)) {
    yield reference.node;
  }
}

export const getMaxTreeDepth = <N extends { children: N[] | null }>(
  node: N
): number =>
  node.children?.length
    ? Math.max(...node.children.map(getMaxTreeDepth)) + 1
    : 1;

export const computeIconColor = (
  backgroundColor: string | undefined,
  iconColor: string | undefined
): string => {
  if (backgroundColor && iconColor) {
    return ensureContrast(backgroundColor, iconColor);
  }
  return iconColor || '';
};

export const getIconColorFromScopeData = (
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  iconModelId: string,
  backgroundModelId: string,
  backgroundIsReference: boolean,
  isNewConditionalFormatting?: boolean
) => {
  const { fill: backgroundColor } = backgroundIsReference
    ? referenceUtils.getReferenceDisplayColorAsSVGAttributes(
        scopeData,
        filterColorData,
        backgroundModelId,
        { useAsBackgroundStyle: true, isNewConditionalFormatting }
      )
    : componentUtils.getComponentDisplayColorAsSVGAttributes(
        scopeData,
        filterColorData,
        backgroundModelId,
        {
          useAsBackgroundStyle: true,
          isNewConditionalFormatting,
        }
      );
  const { stroke: iconColor } =
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      scopeData,
      filterColorData,
      iconModelId,
      { useAsBackgroundStyle: false, isNewConditionalFormatting }
    );
  return computeIconColor(backgroundColor, iconColor);
};
